import LoginService from "~/modules/login/LoginService"
import { defaultCtx } from "~/lib/AjaxCtx"
import DEFAULT_I18N from "~/i18n"

export function makeDefaultState() {
    return {
        email: "",
        phone: "",
        token: "",
        userId: "",
        validationUrl: "",
        validationTimeLeft: null,
        interval: null,
    }
}

export const mutations = {
    setFields(
        state,
        {
            workEmailCensored,
            workPhoneCensored,
            token,
            userId,
            twoFactorAuthenticationAttemptUrl,
        }
    ) {
        state.email = workEmailCensored
        state.phone = workPhoneCensored
        state.token = token
        state.userId = userId
        state.validationUrl = twoFactorAuthenticationAttemptUrl
    },

    setTimeLeft(state, timeLeft) {
        state.validationTimeLeft = timeLeft
    },

    setInterval(state, interval) {
        state.interval = interval
    },
}

export function makeActions({ service, i18n }) {
    i18n = i18n || DEFAULT_I18N.global
    return {
        async init({ dispatch }, { userId, token }) {
            try {
                const {
                    language,
                    twoFactorAuthenticationAttemptUrl,
                    workEmailCensored,
                    workPhoneCensored,
                } = await service.getAuthenticationOptions({ userId, token })

                dispatch("saveData", {
                    workEmailCensored,
                    workPhoneCensored,
                    token,
                    userId,
                    twoFactorAuthenticationAttemptUrl,
                    language,
                })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        type: "error",
                        title: error.message,
                    },
                    { root: true }
                )
            }
        },

        saveData(
            { commit, dispatch },
            {
                workEmailCensored,
                workPhoneCensored,
                token,
                userId,
                twoFactorAuthenticationAttemptUrl,
                language,
            }
        ) {
            commit("setFields", {
                workEmailCensored,
                workPhoneCensored,
                token,
                userId,
                twoFactorAuthenticationAttemptUrl,
            })

            dispatch("loadLocale", language, { root: true })
        },

        async requestCode({ state, dispatch }, option) {
            const { userId, token } = state
            const { twoFactorAuthenticationTimeLeft } =
                await service.requestAuthenticationCode({
                    option,
                    userId,
                    token,
                })

            dispatch("startCountdown", twoFactorAuthenticationTimeLeft)
        },

        async refresh({ dispatch }, { userId, token }) {
            try {
                const {
                    language,
                    twoFactorAuthenticationAttemptUrl,
                    workEmailCensored,
                    workPhoneCensored,
                    twoFactorAuthenticationTimeLeft,
                } = await service.refreshAuthenticationOptions({
                    userId,
                    token,
                })

                dispatch("saveData", {
                    workEmailCensored,
                    workPhoneCensored,
                    token,
                    userId,
                    twoFactorAuthenticationAttemptUrl,
                    language,
                })
                dispatch("startCountdown", twoFactorAuthenticationTimeLeft)
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        type: "error",
                        title: error.message,
                    },
                    { root: true }
                )
            }
        },

        startCountdown({ commit, dispatch, state }, timeLeft) {
            window.clearInterval(state.interval)
            commit("setTimeLeft", timeLeft)

            const interval = setInterval(() => {
                if (state.validationTimeLeft <= 0) {
                    dispatch(
                        "pushNotification",
                        {
                            type: "info",
                            title: i18n.t("common.timeIsUp"),
                        },
                        { root: true }
                    )

                    return window.clearInterval(state.interval)
                }

                commit("setTimeLeft", state.validationTimeLeft - 1)
            }, 1000)

            commit("setInterval", interval)
        },

        async validateCode({ state }, code2fa) {
            const { userId, token } = state

            const response = await service.validateAuthenticationCode({
                userId,
                token,
                code2fa,
            })

            return response
        },

        async deleteAttempt({ dispatch, state }) {
            try {
                const { userId, token } = state
                return await service.deleteAuthenticationAttempt({
                    userId,
                    token,
                })
            } catch (error) {
                dispatch(
                    "pushNotification",
                    {
                        type: "error",
                        title: error.message,
                    },
                    { root: true }
                )
            }
        },
    }
}

export default {
    namespaced: true,
    state: makeDefaultState(),
    mutations,
    actions: makeActions({ service: new LoginService(defaultCtx) }),
}
